import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { formatAddress } from '../lib/blockchain';

const getWalletAddress = (props) =>
  props?.account ||
  props?.wallet?.account ||
  props?.wallet?.address ||
  props?.walletState?.account ||
  props?.walletState?.address ||
  props?.metaMask?.account ||
  props?.metaMask?.address ||
  '';

const formatValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  return `${value}`;
};

export default function Faucet(props) {
  const account = getWalletAddress(props);
  const connectWallet =
    props?.connectWallet ||
    props?.onConnectWallet ||
    props?.wallet?.connectWallet ||
    props?.wallet?.connect ||
    props?.walletState?.connectWallet ||
    props?.walletState?.connect ||
    props?.metaMask?.connectWallet ||
    props?.metaMask?.connect;
  const isConnecting =
    props?.isConnecting ??
    props?.wallet?.isConnecting ??
    props?.wallet?.loading ??
    props?.walletState?.isConnecting ??
    props?.walletState?.loading ??
    props?.metaMask?.isConnecting ??
    props?.metaMask?.loading ??
    false;

  const [supportedChains, setSupportedChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    address: account || '',
    asset: 'iTANI',
    amount: '25',
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      address: account || current.address || '',
    }));
  }, [account]);

  useEffect(() => {
    let cancelled = false;

    async function loadChains() {
      setLoading(true);
      setError('');

      try {
        const response = await api.wallet.supportedChains();
        if (!cancelled) {
          setSupportedChains(Array.isArray(response?.chains) ? response.chains : []);
        }
      } catch (err) {
        if (!cancelled) {
          setSupportedChains([]);
          setError(err?.response?.data?.error || err?.message || 'Unable to load supported chains.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadChains();

    return () => {
      cancelled = true;
    };
  }, []);

  const bridgeChains = useMemo(
    () => supportedChains.filter((chain) => chain?.status === 'bridge'),
    [supportedChains],
  );

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setResult(null);

    try {
      const response = await api.faucet.claim({
        address: form.address,
        asset: form.asset,
        amount: form.amount,
      });

      setResult(response);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Unable to submit faucet claim.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="glass rounded-3xl p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">Faucet</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Claim test assets for iTani flows</h1>
            <p className="mt-2 max-w-3xl text-slate-300">
              This faucet is intended for development and onboarding. Request a small test allocation,
              then use it with Wallet, DEX and Bridge screens.
            </p>
          </div>

          {!account ? (
            <button
              type="button"
              disabled={isConnecting}
              className="rounded-full bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => connectWallet?.()}
            >
              {isConnecting ? 'Connecting…' : 'Connect wallet'}
            </button>
          ) : (
            <div className="rounded-2xl bg-white/5 px-4 py-3 text-right">
              <p className="text-xs uppercase tracking-wide text-slate-400">Destination</p>
              <p className="font-medium text-white">{formatAddress(account)}</p>
            </div>
          )}
        </div>
      </section>

      {error ? (
        <section className="glass rounded-3xl border border-rose-500/30 p-4 text-rose-200">
          {error}
        </section>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <section className="glass rounded-3xl p-6">
          <h2 className="text-xl font-semibold text-white">Request tokens</h2>
          <p className="mt-2 text-sm text-slate-300">
            The backend currently queues a faucet distribution request and returns a tracking identifier.
          </p>

          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <label className="block">
              <span className="mb-2 block text-sm text-slate-400">Destination address</span>
              <input
                name="address"
                value={form.address}
                onChange={onChange}
                placeholder="iTani or EVM wallet address"
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500"
              />
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Asset</span>
                <input
                  name="asset"
                  value={form.asset}
                  onChange={onChange}
                  placeholder="iTANI"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Amount</span>
                <input
                  name="amount"
                  type="number"
                  min="1"
                  step="1"
                  value={form.amount}
                  onChange={onChange}
                  placeholder="25"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500"
                />
              </label>
            </div>

            <div className="rounded-2xl bg-white/5 p-4 text-sm text-slate-300">
              Default faucet flow: queue a small test drop to your wallet, then verify the request id and
              transaction hash returned by the API.
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Claim faucet tokens'}
            </button>
          </form>

          {result ? (
            <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
              <div className="font-semibold text-white">{result.message || 'Faucet claim queued.'}</div>
              <div className="mt-2 space-y-1">
                <div>Claim ID: {formatValue(result.faucet_id)}</div>
                <div>Asset: {formatValue(result.asset)}</div>
                <div>Amount: {formatValue(result.amount)}</div>
                <div>TX hash: {formatValue(result.tx_hash)}</div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="space-y-6">
          <div className="glass rounded-3xl p-6">
            <h2 className="text-xl font-semibold text-white">Supported bridge destinations</h2>
            {loading ? (
              <p className="mt-4 text-slate-300">Loading supported chains…</p>
            ) : bridgeChains.length ? (
              <div className="mt-4 space-y-3">
                {bridgeChains.map((chain) => (
                  <div
                    key={chain.key || chain.id || chain.name}
                    className="rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-medium text-white">{chain.name}</div>
                        <div className="mt-1 text-sm text-slate-400">
                          Native asset: {formatValue(chain.native_currency)}
                        </div>
                      </div>
                      <div className="text-xs uppercase tracking-wide text-cyan-300">
                        {formatValue(chain.status, 'bridge')}
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-slate-400">
                      Custody: {formatValue(chain.custody_address, 'Unavailable')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-slate-300">No external chains were returned by the API.</p>
            )}
          </div>

          <div className="glass rounded-3xl p-6">
            <h2 className="text-xl font-semibold text-white">Usage notes</h2>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              <li>Use the faucet to seed test balances before trying swaps or bridge submissions.</li>
              <li>Bridge custody routes currently expose Ethereum, BNB Chain and Bitcoin metadata.</li>
              <li>Live bridge execution is currently focused on iTani → Ethereum / BNB, while inbound routes rely on custody settlement.</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
